import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./MyAdds.css";
import { API_BASE_URL } from "../config/api";
import Seo from "../Components/Seo";
import { authedFetch } from "../services/apiClient";

export default function MyAds() {
  const navigate = useNavigate();

  const [ads, setAds] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  /* ---------------- FETCH ADS ---------------- */
  const fetchAds = useCallback(async () => {
    const mobileNo = localStorage.getItem("userMobileNumber") || "";

    if (!mobileNo) {
      navigate("/login");
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const res = await authedFetch(
        `${API_BASE_URL}/Product/GetProductsByUserId?MobileNo=${mobileNo}`
      );

      const data = await res.json();

      if (!Array.isArray(data)) {
        throw new Error("Invalid response format");
      }

      // Sort inactive (IsSold=1) to bottom
      const sorted = [...data].sort((a, b) => {
        if (a.IsSold === 1 && b.IsSold !== 1) return 1;
        if (a.IsSold !== 1 && b.IsSold === 1) return -1;
        return 0;
      });

      setAds(sorted);
    } catch (err) {
      console.error(err);
      setError("Failed to load ads. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchAds();
  }, [fetchAds]);

  /* ---------------- DELETE AD ---------------- */
  const deleteAd = async (adId) => {
    if (!window.confirm("Move this product to Recycle Bin?")) return;

    try {
      setIsLoading(true);

      const res = await authedFetch(
        `${API_BASE_URL}/Product/MoveToBin?id=${adId}`,
        { method: "PUT" }
      );

      const text = await res.text();

      if (!res.ok || !text.toLowerCase().includes("success")) {
        throw new Error("Delete failed");
      }

      setAds((prev) => prev.filter((ad) => ad.Id !== adId));
      alert("Product moved to Recycle Bin");
    } catch (err) {
      alert("Failed to delete product");
    } finally {
      setIsLoading(false);
    }
  };

  /* ---------------- TOGGLE STATUS ---------------- */
  const toggleProductStatus = async (productId, currentStatus) => {
    const newStatus = currentStatus === 0 ? 1 : 0;
    const label = newStatus === 1 ? "Inactive" : "Active";

    if (!window.confirm(`Mark product as ${label}?`)) return;

    try {
      setIsLoading(true);

// Backend IsSold ek [HttpPut] endpoint hai — GET 405 deta hai.
      const res = await authedFetch(
        `${API_BASE_URL}/Product/IsSold?Id=${productId}&IsSold=${newStatus}`,
        { method: "PUT" }
      );

      if (!res.ok) throw new Error();

      setAds((prev) =>
        [...prev]
          .map((ad) =>
            ad.Id === productId ? { ...ad, IsSold: newStatus } : ad
          )
          .sort((a, b) => {
            if (a.IsSold === 1 && b.IsSold !== 1) return 1;
            if (a.IsSold !== 1 && b.IsSold === 1) return -1;
            return 0;
          })
      );

      alert(`Product is now ${label}`);
    } catch (err) {
      alert("Failed to update status");
    } finally {
      setIsLoading(false);
    }
  };

  /* ---------------- TOGGLE PREMIUM ---------------- */
  const togglePremium = async (adId, isPremium) => {
    const doing = !(Number(isPremium) === 1);
    const label = doing ? "Premium banayein (₹99)" : "Normal banayein";
    if (!window.confirm(`Product ko ${label} ka kar rahe hain?`)) return;

    try {
      setIsLoading(true);
      const res = await authedFetch(
        `${API_BASE_URL}/Product/toggle-premium?id=${adId}`,
        { method: "POST" }
      );
      if (!res.ok) throw new Error();
      setAds((prev) =>
        prev.map((ad) =>
          ad.Id === adId
            ? { ...ad, isPremium: doing ? 1 : 0 }
            : ad
        )
      );
      alert(doing ? "Product ab Premium hai — top par dikhega!" : "Product ab Normal category me hai.");
    } catch (err) {
      alert("Premium update failed");
    } finally {
      setIsLoading(false);
    }
  };

  /* ---------------- PDF EXPORT ---------------- */
  const exportPdf = () => {
    if (!ads.length) return;
    const w = window.open("", "_blank");
    if (!w) {
      alert("Popup blocked — browser allow karein.");
      return;
    }
    const rows = ads
      .map(
        (a, i) =>
          `<tr><td>${i + 1}</td><td>${(a.ProductName || "").replace(/</g, "&lt;")}</td><td>₹${a.Price}</td><td>${a.Quantity || 1}</td><td>${a.IsSold === 1 ? "Sold/Inactive" : "Active"}</td></tr>`
      )
      .join("");
    w.document.write(`<html><head><title>My Listings - AIO INDIA</title>
      <style>body{font-family:Arial,Helvetica,sans-serif;padding:24px;color:#111}
      h1{font-size:20px;margin-bottom:4px}h2{font-size:13px;color:#555;font-weight:400;margin:0 0 16px}
      table{width:100%;border-collapse:collapse;font-size:13px}
      th,td{border:1px solid #ccc;padding:8px;text-align:left}
      th{background:#4f46e5;color:#fff}.tot{font-weight:700;margin-top:12px}</style></head>
      <body><h1>AIO INDIA - My Listings</h1><h2>Total: ${ads.length} item(s)</h2>
      <table><thead><tr><th>#</th><th>Product</th><th>Price</th><th>Qty</th><th>Status</th></tr></thead>
      <tbody>${rows}</tbody></table>
      <div class="tot">Grand Total Value: ₹${ads.reduce((s, a) => s + (Number(a.Price) || 0) * (Number(a.Quantity) || 1), 0)}</div>
      </body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 400);
  };

  /* ---------------- UI STATES ---------------- */
  if (isLoading) {
    return (
      <div className="center">
        <div className="spinner" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="center">
        <p className="error">{error}</p>
        <button onClick={fetchAds}>Retry</button>
      </div>
    );
  }

  /* ---------------- RENDER ---------------- */
  return (
    <div className="myads-page">
      <Seo title="My Listings & Ads" description="Apne AIO India pe post kiye hue ads dekhna, edit karna, premium banayein ya delete karein." path="/myadds" />
      <div className="header-myads">
        <h2>My Listings</h2>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <span>{ads.length} item(s) posted</span>
          {ads.length > 0 && (
            <button
              onClick={exportPdf}
              style={{
                padding: "7px 14px", borderRadius: "8px", border: "none",
                background: "#16a34a", color: "#fff", fontWeight: 700, cursor: "pointer",
              }}
            >
              📄 Export PDF
            </button>
          )}
        </div>
      </div>

      {ads.length === 0 ? (
        <div className="empty">
          <p>No ads posted yet</p>
          <button onClick={() => navigate("/AddProduct")}>
            Post Your First Ad
          </button>
        </div>
      ) : (
        <div className="list-myads">
          {ads.map((item) => (
            <div
              key={item.Id}
              className={`card-myads ${item.IsSold === 1 ? "inactive" : ""}`}
              onClick={() =>
                navigate(`/edit-product/${item.Id}`)
              }
            >
              {/* IMAGE */}
              <div className="thumb">
                <img
                  src={item.ProductImage || "/placeholder.png"}
                  alt=""
                />
                {item.IsSold === 1 && <span className="sold">SOLD</span>}
                {Number(item.isPremium) === 1 && <span className="sold" style={{ background: "#7c3aed" }}>PREMIUM</span>}
              </div>

              {/* CONTENT */}
              <div className="content">
                <h4>{item.ProductName || "Unnamed Ad"}</h4>
                <p className="price">₹{item.Price}</p>
                {item.Quantity > 0 && (
                    <p style={{ fontSize: "12px", color: "#475569", marginTop: "2px" }}>
                        Qty: {item.Quantity}
                    </p>
                )}

                <button
                  className={`status ${item.IsSold === 1 ? "inactive-btn" : "active-btn"
                    }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleProductStatus(item.Id, item.IsSold);
                  }}
                >
                  {item.IsSold === 1 ? "Inactive" : "Active"}
                </button>
              </div>

              {/* ACTIONS */}
              <div className="actions-conainer">
                <div className="actions">
                  <button
                    className={Number(item.isPremium) === 1 ? "premium-on" : ""}
                    onClick={(e) => {
                      e.stopPropagation();
                      togglePremium(item.Id, item.isPremium);
                    }}
                    style={{
                      ...(Number(item.isPremium) === 1
                        ? { background: "#7c3aed", color: "#fff", border: "none" }
                        : {}),
                    }}
                  >
                    {Number(item.isPremium) === 1 ? "Premium" : "Premium ₹99"}
                  </button>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/edit-product/${item.Id}`);
                    }}
                  >
                    Edit
                  </button>

                  <button
                    className="delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteAd(item.Id);
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
